import React, { useEffect, useState } from "react";
import { Download } from "lucide-react";
import { toast } from "sonner";
import api, { downloadFile, formatApiError } from "../lib/api";
import { money } from "../lib/format";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const ALL = "ALL";
const monthLabel = (month) =>
  new Date(`${month}-01T00:00:00`).toLocaleDateString("en-US", { month: "short", year: "numeric" });
const ratePct = (rate) => (rate === null || rate === undefined ? "—" : `${(rate * 100).toFixed(2)}%`);

/** Carrier, Employees First, tech-credit and Purchasing Power fees rebuilt from the rates table. */
export default function FeeCalculation({ employers }) {
  const [company, setCompany] = useState("");
  const [from, setFrom] = useState(ALL);
  const [to, setTo] = useState(ALL);
  const [report, setReport] = useState(null);

  useEffect(() => {
    if (!company && employers.length) setCompany(employers[0]);
  }, [employers, company]);

  useEffect(() => {
    if (!company) return;
    api.get("/admin/rates/fees", {
      params: { company, from_month: from === ALL ? "" : from, to_month: to === ALL ? "" : to },
    })
      .then(({ data }) => setReport(data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, [company, from, to]);

  const exportCsv = async () => {
    try {
      await downloadFile("/admin/rates/fees/export", "fee-calculation.csv", {
        company, from_month: from === ALL ? "" : from, to_month: to === ALL ? "" : to,
      });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const months = report?.months_available || [];
  const rows = report?.rows || [];
  const totals = report?.totals || {};

  const picker = (label, value, onChange, testId) => (
    <div className="min-w-[150px]">
      <label className="field-label">{label}</label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="!rounded-none !border-x-0 !border-t-0 !bg-transparent !px-0" data-testid={testId}>
          <SelectValue/>
        </SelectTrigger>
        <SelectContent className="!rounded-none max-h-72">
          <SelectItem value={ALL}>All months</SelectItem>
          {months.map((month) => <SelectItem key={month} value={month}>{monthLabel(month)}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <div data-testid="fee-calculation">
      <div className="flex flex-wrap items-end gap-4 mb-3">
        <div className="min-w-[220px]">
          <label className="field-label">Employer</label>
          <Select value={company} onValueChange={setCompany}>
            <SelectTrigger className="!rounded-none !border-x-0 !border-t-0 !bg-transparent !px-0" data-testid="fees-company">
              <SelectValue placeholder="Select employer"/>
            </SelectTrigger>
            <SelectContent className="!rounded-none max-h-72">
              {employers.map((name) => <SelectItem key={name} value={name}>{name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {picker("From", from, setFrom, "fees-from")}
        {picker("To", to, setTo, "fees-to")}
        <button onClick={exportCsv} className="btn-ghost flex items-center gap-2" data-testid="fees-export">
          <Download size={14}/> Export CSV
        </button>
      </div>
      <p className="text-xs max-w-3xl mb-5" style={{ color: "var(--muted)" }}>
        Fees are the in-force rate applied to that month's {report?.plan_type || "ERISA"} premium.
        Premium tax is tiered on monthly premium{report && !report.prem_tax_liable ? " and this employer is not premium-tax liable" : ""}.
        Purchasing Power is maintained manually in the workbook, so its calculated figure is for reference.
      </p>

      <div className="border overflow-x-auto" style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
        <table className="w-full text-sm min-w-[1100px]" data-testid="fees-table">
          <thead>
            <tr className="border-b eyebrow text-left" style={{ borderColor: "var(--border)" }}>
              <th className="px-4 py-3">Month</th>
              <th className="px-4 py-3 text-right">Premium (fee base)</th>
              <th className="px-4 py-3 text-right">E1 rate</th>
              <th className="px-4 py-3 text-right">E1 fees</th>
              <th className="px-4 py-3 text-right">Carrier rate</th>
              <th className="px-4 py-3 text-right">Carrier fees</th>
              <th className="px-4 py-3 text-right">Premium tax</th>
              <th className="px-4 py-3 text-right">PP fees</th>
              <th className="px-4 py-3 text-right">Total (calc)</th>
              <th className="px-4 py-3 text-right">Total (feed)</th>
              <th className="px-4 py-3 text-right">Variance</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.month} className="border-b" style={{ borderColor: "var(--border)" }} data-testid={`fees-row-${row.month}`}>
                <td className="px-4 py-2.5 font-medium" style={{ color: "var(--ink)" }}>{monthLabel(row.month)}</td>
                <td className="px-4 py-2.5 text-right">{money(row.fee_premium)}</td>
                <td className="px-4 py-2.5 text-right text-xs" style={{ color: "var(--muted)" }}>{ratePct(row.E1Fees_rate)}</td>
                <td className="px-4 py-2.5 text-right">{money(row.E1Fees_calc)}</td>
                <td className="px-4 py-2.5 text-right text-xs" style={{ color: "var(--muted)" }}>{ratePct(row.CarrierFees_rate)}</td>
                <td className="px-4 py-2.5 text-right">{money(row.CarrierFees_calc)}</td>
                <td className="px-4 py-2.5 text-right">{money(row.ptaxes_calc)}</td>
                <td className="px-4 py-2.5 text-right">{money(row.PP_calc)}</td>
                <td className="px-4 py-2.5 text-right font-heading font-bold" style={{ color: "var(--ink)" }}>{money(row.fees_calc)}</td>
                <td className="px-4 py-2.5 text-right">{money(row.fees_feed)}</td>
                <td className="px-4 py-2.5 text-right" style={{ color: Math.abs(row.variance) > 1 ? "#B45309" : "var(--muted)" }}>
                  {money(row.variance)}
                </td>
              </tr>
            ))}
            {!rows.length && (
              <tr><td colSpan={11} className="p-10 text-center text-sm" style={{ color: "var(--muted)" }} data-testid="fees-empty">
                No financial months on file for this employer yet.
              </td></tr>
            )}
          </tbody>
          {!!rows.length && (
            <tfoot>
              <tr className="font-heading font-bold" style={{ color: "var(--ink)" }} data-testid="fees-totals">
                <td className="px-4 py-3">Total</td>
                <td className="px-4 py-3 text-right">{money(totals.fee_premium)}</td>
                <td/>
                <td className="px-4 py-3 text-right">{money(totals.E1Fees_calc)}</td>
                <td/>
                <td className="px-4 py-3 text-right">{money(totals.CarrierFees_calc)}</td>
                <td className="px-4 py-3 text-right">{money(totals.ptaxes_calc)}</td>
                <td className="px-4 py-3 text-right">{money(totals.PP_calc)}</td>
                <td className="px-4 py-3 text-right">{money(totals.fees_calc)}</td>
                <td className="px-4 py-3 text-right">{money(totals.fees_feed)}</td>
                <td className="px-4 py-3 text-right">{money(totals.variance)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
